import {
  useEffect,
  useMemo,
  useRef,
  useState,
  type KeyboardEvent,
  type ReactNode,
  type RefObject,
} from "react";
import { RouterLink as Link } from "../../router";
import { ArrowRight, ArrowLeft, Sparkles } from "../../icons";

/** The icon-component shape every barrel glyph satisfies (lucide-at-1px bridge). */
type IconComponent = typeof Sparkles;
import { cn } from "../../utils";
import { Button } from "../../button";
import { Badge } from "../../badge";
import { Textarea } from "../../textarea";

/**
 * SetupDriver — the shared "walk one card at a time" interaction primitive.
 *
 * Extracted from the Play driver so every "Set up {X}" flow (the Play queue,
 * section onboarding, the section cold-start) walks the user through work with
 * ONE consistent surface instead of a fork per feature. Presentational only:
 * the host owns the modal/sheet shell, data fetching, and persistence.
 *
 * Two card shapes:
 *  • action — a badge + icon + title/body + reasons + a CTA (link or button)
 *             that routes the user to the right surface.
 *  • prompt — a question answered inline. The answer commits optimistically
 *             (advance immediately, persist in the background; a failed save
 *             rolls back to the card with the draft + a retry note). The input
 *             defaults to a free-text textarea but any card can supply its own
 *             via `renderInput` (e.g. a typed brief-field editor).
 *
 * Mirrors the Workflows builder wizard chrome: tidy top bar, segmented
 * progress, cards that fade+slide in, full keyboard operability (S=skip,
 * N=not-relevant, ←=back).
 */

export type SetupCardIntent = "info" | "success" | "warning" | "danger" | "neutral";

export type SetupSignal = "shown" | "started" | "completed" | "skipped" | "dismissed";

export interface SetupInputContext {
  value: unknown;
  setValue: (v: unknown) => void;
  /** Commit the current value (same as pressing Save / ⌘↵). */
  save: () => void;
  error: string | null;
  disabled: boolean;
}

interface SetupCardBase {
  /** Stable key — drives the re-mount animation and per-card draft scoping. */
  key: string;
  title: string;
  body?: string;
  /** Quiet "why this surfaced" lines (shown under action cards). */
  reasons?: string[];
  /** Category badge (action cards). */
  badge?: { label: string; intent: SetupCardIntent };
  /** Leading icon (action cards). */
  icon?: IconComponent;
  /** Payoff hint shown beside the badge ("Saves ~2h/week"). */
  payoff?: string;
}

export interface SetupActionCard extends SetupCardBase {
  mode: "action";
  ctaLabel?: string;
  ctaHref?: string;
  /** Whether the §2.5 Dive-in verb is meaningful for this card (it references a
   *  concrete object). Set by the Play host; gates the host-controlled Dive-in
   *  button (with `onDive`). Undefined/false ⇒ no Dive-in. */
  canDive?: boolean;
  /** The event that produced this recommendation (Normal Core §6.5.8). When set,
   *  the Play takeover shows a "Why?" trigger that walks this event's causal
   *  ancestry (`/api/events/chain`) into a CausalTraceView. Optional/additive —
   *  only event-derived cards (e.g. commitment-risk intel) carry it; everything
   *  else omits it and the affordance doesn't render. The structured counterpart
   *  to the quiet `reasons` lines. */
  causationUid?: string;
  /** Aggregate kind of the source event ("commitment"), folded into the chain
   *  query so the trace includes that aggregate's lifecycle history for depth. */
  causationKind?: string;
}

export interface SetupPromptCard extends SetupCardBase {
  mode: "prompt";
  /** The question shown as the card headline. Falls back to `title`. */
  question?: string;
  /** Optional sub-label under the question (e.g. field label · required). */
  sublabel?: string;
  /** Starting value handed to the input. */
  initialValue?: unknown;
  placeholder?: string;
  /** Custom input. Omitted → a free-text autosizing textarea (string value). */
  renderInput?: (ctx: SetupInputContext) => ReactNode;
  /** Gate the Save button. Default: a non-empty trimmed string. */
  canSave?: (value: unknown) => boolean;
  /** Optional node rendered beneath the input (e.g. a provenance row). */
  footer?: ReactNode;
}

export type SetupCard = SetupActionCard | SetupPromptCard;

export interface SetupDriverCopy {
  finishTitle?: (cleared: number) => string;
  finishBody?: (hasMore: boolean) => string;
  /** Empty-state line when there are no cards at all. */
  emptyTitle?: string;
  emptyBody?: string;
}

export interface SetupDriverProps {
  cards: SetupCard[];
  /** Total available (≥ cards.length when the host capped the set). */
  total?: number;
  /**
   * Card to open on. Lets a host (e.g. the home "What to do next" list) launch
   * the walk focused on a specific card instead of the top of the queue.
   * Clamped to range; defaults to 0.
   */
  initialIndex?: number;
  /** Record a lifecycle signal. Optional — flows without a ledger omit it. */
  onSignal?: (card: SetupCard, signal: SetupSignal, reason?: string) => void;
  /** Persist a prompt answer. Resolves on success; reject to trigger rollback. */
  onAnswer?: (card: SetupPromptCard, value: unknown) => Promise<void> | void;
  /** The user finished the walk and pressed Done. */
  onExhausted?: () => void;
  /**
   * When provided: pressing Back on the first card returns here, and the finish
   * panel offers "Pick another". Used by Play to bounce back to its category
   * launcher; omitted by linear flows.
   */
  onRestart?: () => void;
  /**
   * Host-controlled action verbs (Three-Layer PRD §2.3 — the Play global driver).
   * When `onAct` is provided, an ACTION card's primary "Do" calls it (the host
   * resolves the route + navigates) instead of following `ctaHref` as a Link;
   * `onDive` renders the §2.5 Dive-in button (open the object's owning Domain) and
   * `onDelegate` the Delegate button. All optional — linear setup flows omit them
   * and keep the legacy ctaHref Link. Prompt (answerable) cards are unaffected.
   */
  onAct?: (card: SetupActionCard) => void;
  onDive?: (card: SetupActionCard) => void;
  onDelegate?: (card: SetupActionCard) => void;
  /** Show the floating "Not relevant" dismiss affordance. Default true. */
  showDismiss?: boolean;
  copy?: SetupDriverCopy;
  /**
   * Replace the driver's default chrome with a custom shell, WITHOUT forking the
   * walk logic (the sanctioned "compose, don't fork a stepper" path). When set,
   * the driver computes all state/transitions exactly as normal — impressions,
   * the optimistic answer save + rollback, keyboard shortcuts, finish/empty — and
   * hands them to `renderShell` as a `SetupShellContext` to paint however it likes
   * (e.g. the Play fullscreen "Screening Room" takeover). The shell MUST spread
   * `ref={ctx.rootRef}` + `onKeyDown={ctx.onKeyDown}` on its root and put
   * `data-setup-focus` on its primary control so autofocus + shortcuts work. The
   * default (unset) renders the inline card chrome unchanged. */
  renderShell?: (ctx: SetupShellContext) => ReactNode;
}

/**
 * The full walk state + transitions handed to a `renderShell`. Lets a host paint
 * a custom surface over the shared walk brain. The shell branches on
 * `isEmpty`/`done`/`isPrompt` to render the empty, finish, prompt, and action
 * states; it owns ALL presentation, the driver owns ALL logic.
 */
export interface SetupShellContext {
  cards: SetupCard[];
  current: SetupCard | null;
  /** 0-based position of the current card. */
  index: number;
  /** Cards in THIS walk (the set being walked). */
  count: number;
  /** Total available (≥ count when the host capped the set). */
  total: number;
  /** total − count — drives a "more queued" hint. */
  remaining: number;
  isEmpty: boolean;
  done: boolean;
  isPrompt: boolean;
  /** How many cards the user decided on — the finish-panel count. */
  decidedCount: number;
  // prompt input
  value: unknown;
  setValue: (v: unknown) => void;
  error: string | null;
  canSave: boolean;
  // transitions (same as the inline chrome's buttons)
  save: () => void;
  act: () => void;
  /** Post a 'completed' signal on the current card (the same signal path as
   *  dismiss, different verdict). For cards whose primary verb is "keep it as
   *  done" in place rather than "navigate to do it" — e.g. a Learning Loop
   *  memory card's "Keep this". */
  complete: () => void;
  skip: () => void;
  dismiss: () => void;
  back: () => void;
  canBack: boolean;
  // host-controlled verbs (Play §2.3)
  onAct?: (card: SetupActionCard) => void;
  onDive?: (card: SetupActionCard) => void;
  onDelegate?: (card: SetupActionCard) => void;
  onExhausted?: () => void;
  onRestart?: () => void;
  showDismiss: boolean;
  // wiring the shell must attach for autofocus + keyboard
  rootRef: RefObject<HTMLDivElement | null>;
  onKeyDown: (e: KeyboardEvent) => void;
}

const isFilledString = (v: unknown): boolean =>
  typeof v === "string" && v.trim().length > 0;

/**
 * The shared card-walk progress affordance — one h-1 segment per card, filled
 * up to the active index. The single implementation behind every stepped walk:
 * SetupDriver (setup/action walks with persistence + lifecycle signals) and
 * AskUserQuestions (the pure Q&A flow with controlled answers). If you're
 * building a third walk, compose one of those two — don't fork a stepper
 * (CLAUDE.md frontend conventions; SetupDriver is the sanctioned primitive).
 */
export function SegmentedProgress({
  count,
  index,
  className,
}: {
  count: number;
  index: number;
  className?: string;
}) {
  return (
    <div className={cn("flex gap-1", className)} aria-hidden>
      {Array.from({ length: count }, (_, i) => (
        <span
          key={i}
          className={cn(
            "h-1 flex-1 rounded-full transition-colors",
            i < index ? "bg-primary/60" : i === index ? "bg-primary" : "bg-border",
          )}
        />
      ))}
    </div>
  );
}

/**
 * True when a focused control owns its own keystrokes, so the card's single-key
 * shortcuts (s=skip, n=not-relevant) must stand down. Covers plain inputs, a
 * native <select>, contentEditable surfaces, and — the case the INPUT/TEXTAREA
 * guard missed — a Radix <Select> trigger (role="combobox") or open listbox,
 * which run their own type-ahead; otherwise typing in a select would skip or
 * dismiss the card out from under the user.
 */
const ownsKeystrokes = (target: EventTarget | null): boolean => {
  if (!(target instanceof HTMLElement)) return false;
  if (target.isContentEditable) return true;
  const tag = target.tagName;
  if (tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT") return true;
  return target.closest('[role="combobox"],[role="listbox"],[role="menu"]') !== null;
};

export function SetupDriver({
  cards,
  total,
  initialIndex,
  onSignal,
  onAnswer,
  onExhausted,
  onRestart,
  onAct,
  onDive,
  onDelegate,
  showDismiss = true,
  copy,
  renderShell,
}: SetupDriverProps) {
  // Seed once at mount — hosts that want to re-target re-key the driver (the
  // Play button keys on session, the home launcher keys on the chosen action).
  const [idx, setIdx] = useState(() =>
    Math.min(Math.max(0, initialIndex ?? 0), Math.max(0, cards.length - 1)),
  );
  const [done, setDone] = useState(false);
  const [value, setValue] = useState<unknown>("");
  const [error, setError] = useState<string | null>(null);

  const n = cards.length;
  const current = cards[idx];
  const total_ = total ?? n;
  const remaining = Math.max(0, total_ - n);
  const isPrompt = current?.mode === "prompt";

  const decidedRef = useRef<Set<string>>(new Set());
  const shownRef = useRef<Set<string>>(new Set());
  const rootRef = useRef<HTMLDivElement>(null);
  // A draft whose background save failed — restored (with a retry note) when the
  // optimistic advance rolls back to its card. Keyed by card so a stale restore
  // can't bleed onto a different card.
  const failedDraftRef = useRef<{ key: string; value: unknown } | null>(null);
  // Live position, read inside the fire-and-forget save's late rejection (whose
  // closure captured a stale idx/done) to decide whether a rollback would still
  // land on the card the user is actually looking at.
  const posRef = useRef({ idx, done });
  posRef.current = { idx, done };

  // Impression — fire once per visible card.
  useEffect(() => {
    if (done || !current) return;
    if (shownRef.current.has(current.key)) return;
    shownRef.current.add(current.key);
    onSignal?.(current, "shown");
  }, [current, done, onSignal]);

  // Fresh card → seed the input draft + focus the primary affordance. A rolled
  // back failed save restores its draft + retry note instead of clearing.
  useEffect(() => {
    if (done || !current) return;
    const seed =
      current.mode === "prompt" ? (current.initialValue ?? "") : "";
    const failed = failedDraftRef.current;
    if (failed && failed.key === current.key) {
      setValue(failed.value);
      setError("Couldn't save that. Try again.");
      failedDraftRef.current = null;
    } else {
      setValue(seed);
      setError(null);
    }
    const el = rootRef.current?.querySelector<HTMLElement>("[data-setup-focus]");
    el?.focus({ preventScroll: true });
  }, [current?.key, done]);

  const decide = (signal: SetupSignal, reason?: string) => {
    if (!current) return;
    onSignal?.(current, signal, reason);
    decidedRef.current.add(current.key);
    if (idx + 1 >= n) setDone(true);
    else setIdx(idx + 1);
  };

  const act = () => decide("started");
  const complete = () => decide("completed");
  const skip = () => decide("skipped");
  const dismiss = () => decide("dismissed");
  const back = () => {
    if (done) {
      setDone(false);
      return;
    }
    if (idx === 0) onRestart?.();
    else setIdx((i) => i - 1);
  };

  const canSave = (() => {
    if (current?.mode !== "prompt") return false;
    const gate = current.canSave ?? isFilledString;
    return gate(value);
  })();

  const save = () => {
    if (current?.mode !== "prompt" || !canSave) return;
    const card = current;
    const committed = value;
    const from = idx;
    setError(null);
    decidedRef.current.add(card.key);
    // Optimistic: advance immediately, persist in the background. On failure,
    // roll back to this card — the reset effect restores the draft + retry note.
    if (from + 1 >= n) setDone(true);
    else setIdx(from + 1);
    void (async () => {
      try {
        await onAnswer?.(card, committed);
        onSignal?.(card, "completed");
      } catch {
        failedDraftRef.current = { key: card.key, value: committed };
        decidedRef.current.delete(card.key);
        // Positional-safe rollback. The optimistic advance parked the user at
        // the "immediate next" spot — card `from + 1`, or the finish panel when
        // `from` was the last card. A fire-and-forget save can reject only after
        // the user has walked several cards on; an absolute setIdx(from) would
        // then yank them backward and discard their position (and reset any
        // draft on the card they're now editing). So only re-pin if they're
        // STILL parked there. Otherwise leave them — failedDraftRef re-surfaces
        // the retry note the next time they land on `from`.
        const { idx: liveIdx, done: liveDone } = posRef.current;
        const stillParked = from + 1 >= n ? liveDone : liveIdx === from + 1;
        if (stillParked) {
          setDone(false);
          setIdx(from);
        }
      }
    })();
  };

  // Keyboard shortcuts on a card. Typing in a focused editable/typeahead
  // control is exempt — see ownsKeystrokes (covers inputs, native selects, a
  // Radix <Select> trigger, and contentEditable surfaces).
  const onKeyDown = (e: KeyboardEvent) => {
    if (done) return;
    if (ownsKeystrokes(e.target)) return;
    const k = e.key.toLowerCase();
    if (k === "s") {
      e.preventDefault();
      skip();
    } else if (k === "n" && showDismiss) {
      e.preventDefault();
      dismiss();
    } else if (e.key === "ArrowLeft") {
      e.preventDefault();
      back();
    }
  };

  // ---- Custom shell (compose, don't fork): hand the whole walk to a host skin.
  // Placed after every hook + handler so the shell gets the live state; the
  // default chrome below is byte-identical when `renderShell` is unset. ----
  if (renderShell) {
    return (
      <>
        {renderShell({
          cards,
          current: current ?? null,
          index: idx,
          count: n,
          total: total_,
          remaining,
          isEmpty: n === 0,
          done,
          isPrompt,
          decidedCount: decidedRef.current.size,
          value,
          setValue,
          error,
          canSave,
          save,
          act,
          complete,
          skip,
          dismiss,
          back,
          canBack: !(idx === 0 && !onRestart),
          onAct,
          onDive,
          onDelegate,
          onExhausted,
          onRestart,
          showDismiss,
          rootRef,
          onKeyDown,
        })}
      </>
    );
  }

  // ---- Empty ----
  if (n === 0) {
    return (
      <div className="flex flex-col items-center justify-center gap-2 py-10 text-center">
        <div className="flex h-10 w-10 items-center justify-center rounded-full bg-secondary/60">
          <Sparkles strokeWidth={1.5} className="h-5 w-5 text-muted-foreground" />
        </div>
        <p className="type-body font-medium text-foreground">
          {copy?.emptyTitle ?? "You're all caught up"}
        </p>
        <p className="type-body text-muted-foreground">
          {copy?.emptyBody ?? "Nothing pressing right now. New work shows up here as it appears."}
        </p>
      </div>
    );
  }

  // ---- Finish ----
  if (done) {
    const cleared = decidedRef.current.size;
    const hasMore = remaining > 0;
    return (
      <div className="flex flex-col items-center justify-center gap-3 py-8 text-center duration-200 animate-in fade-in-0 zoom-in-95">
        <div className="flex h-12 w-12 items-center justify-center rounded-full bg-primary/10">
          <Sparkles strokeWidth={1.5} className="h-6 w-6 text-primary" />
        </div>
        <div className="flex flex-col gap-1">
          <p className="type-body font-medium text-foreground">
            {copy?.finishTitle?.(cleared) ??
              `Nice — you cleared ${cleared} ${cleared === 1 ? "item" : "items"}`}
          </p>
          <p className="type-body text-muted-foreground">
            {copy?.finishBody?.(hasMore) ??
              (hasMore
                ? "More is queued whenever you want it."
                : "That's everything pressing right now. New work shows up here as it appears.")}
          </p>
        </div>
        <div className="flex items-center gap-2 pt-1">
          {hasMore && onRestart ? (
            <Button variant="outline" onClick={onRestart} className="justify-center">
              Pick another
            </Button>
          ) : null}
          <Button data-setup-focus onClick={() => onExhausted?.()} className="justify-center">
            Done
          </Button>
        </div>
      </div>
    );
  }

  if (!current) return null;

  const headline = isPrompt
    ? ((current as SetupPromptCard).question ?? current.title)
    : current.title;
  const Icon = current.icon;
  const reasons = current.reasons ?? [];

  // ---- Cards ----
  return (
    <div ref={rootRef} onKeyDown={onKeyDown} className="flex flex-col gap-6">
      {/* top bar: Back (left) · counter (right). The counter clears a hosting
          dialog's top-right close button (pr-7) so the driver reads cleanly
          inside a modal (Play) as well as inline (section onboarding). */}
      <div className="flex flex-col gap-2">
        <div className="flex items-center justify-between pr-7">
          <button
            type="button"
            onClick={back}
            disabled={idx === 0 && !onRestart}
            className="flex items-center gap-1 type-subtext text-muted-foreground hover:text-foreground disabled:opacity-0"
            aria-label={idx === 0 ? "Back to start" : "Previous"}
          >
            <ArrowLeft strokeWidth={1.5} className="h-3.5 w-3.5" />
            Back
          </button>
          <span className="type-subtext tabular-nums text-muted-foreground">
            {idx + 1}/{n}
          </span>
        </div>
        <SegmentedProgress count={cards.length} index={idx} />
      </div>

      {/* the card — re-keyed so each one fades + slides in */}
      <div
        key={current.key}
        className="flex flex-col gap-4 duration-200 animate-in fade-in-0 slide-in-from-bottom-1"
      >
        {isPrompt ? (
          <div className="flex flex-col gap-0.5">
            <h2 className="type-title font-medium text-foreground">
              {headline}
            </h2>
            {(current as SetupPromptCard).sublabel ? (
              <p className="type-body text-muted-foreground">
                {(current as SetupPromptCard).sublabel}
              </p>
            ) : null}
          </div>
        ) : (
          <>
            {current.badge || current.payoff ? (
              <div className="flex items-center gap-2">
                {current.badge ? (
                  <Badge variant={current.badge.intent}>{current.badge.label}</Badge>
                ) : null}
                {current.payoff ? (
                  <span className="type-body text-muted-foreground">{current.payoff}</span>
                ) : null}
              </div>
            ) : null}
            <div className="flex items-start gap-3">
              {Icon ? (
                <div className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-secondary/60">
                  <Icon strokeWidth={1.5} className="h-4 w-4 text-foreground" />
                </div>
              ) : null}
              <div className="flex flex-col gap-1">
                <h2 className="type-title font-medium text-foreground">
                  {current.title}
                </h2>
                {current.body ? (
                  <p className="type-body leading-body text-muted-foreground">
                    {current.body}
                  </p>
                ) : null}
              </div>
            </div>
          </>
        )}

        {!isPrompt && reasons.length > 0 ? (
          <p className="type-subtext text-muted-foreground">{reasons.slice(0, 2).join(" · ")}</p>
        ) : null}

        {isPrompt ? (
          <div className="flex flex-col gap-2">
            {(current as SetupPromptCard).renderInput ? (
              (current as SetupPromptCard).renderInput!({
                value,
                setValue,
                save,
                error,
                disabled: false,
              })
            ) : (
              <Textarea
                autosize
                rows={1}
                data-setup-focus
                value={typeof value === "string" ? value : ""}
                onChange={(e) => {
                  setValue(e.target.value);
                  if (error) setError(null);
                }}
                onKeyDown={(e) => {
                  // Enter inserts a newline; ⌘/Ctrl+Enter saves.
                  if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) {
                    e.preventDefault();
                    save();
                  }
                }}
                className="min-h-9"
                placeholder={(current as SetupPromptCard).placeholder ?? "Type your answer…"}
                aria-label="Your answer"
              />
            )}
            {(current as SetupPromptCard).footer}
            {error ? <span className="type-subtext text-destructive">{error}</span> : null}
          </div>
        ) : null}
      </div>

      {/* actions — Skip left · primary right (with arrow) */}
      <div className="flex items-center justify-between gap-2">
        <Button variant="outline" onClick={skip} className="justify-center">
          Skip
        </Button>
        {isPrompt ? (
          <Button onClick={save} disabled={!canSave} className="justify-center">
            Save
            <ArrowRight strokeWidth={1.5} className="h-4 w-4" />
          </Button>
        ) : onAct ? (
          // Host-controlled verb cluster (Play global driver, §2.3): Do routes
          // through the host's resolver; Dive-in opens the object's owning Domain;
          // Delegate hands it to the agent fleet. The host owns navigation + the
          // 'started' signal, so these buttons don't advance locally.
          <div className="flex items-center gap-2">
            {onDelegate ? (
              <Button
                variant="ghost"
                onClick={() => onDelegate(current as SetupActionCard)}
                className="justify-center"
              >
                Delegate
              </Button>
            ) : null}
            {onDive && (current as SetupActionCard).canDive ? (
              <Button
                variant="outline"
                onClick={() => onDive(current as SetupActionCard)}
                className="justify-center"
              >
                Dive in
              </Button>
            ) : null}
            <Button
              data-setup-focus
              onClick={() => onAct(current as SetupActionCard)}
              className="justify-center"
            >
              {(current as SetupActionCard).ctaLabel ?? "Do it"}
              <ArrowRight strokeWidth={1.5} className="h-4 w-4" />
            </Button>
          </div>
        ) : (current as SetupActionCard).ctaHref ? (
          <Button asChild className="justify-center">
            <Link data-setup-focus href={(current as SetupActionCard).ctaHref!} onClick={act}>
              {(current as SetupActionCard).ctaLabel ?? "Do it"}
              <ArrowRight strokeWidth={1.5} className="h-4 w-4" />
            </Link>
          </Button>
        ) : (
          <Button data-setup-focus onClick={act} className="justify-center">
            {(current as SetupActionCard).ctaLabel ?? "Mark started"}
            <ArrowRight strokeWidth={1.5} className="h-4 w-4" />
          </Button>
        )}
      </div>

      {/* "Not relevant" floats just below the panel, on the backdrop (absolute
          resolves to the fixed shell — same trick the outside X uses). */}
      {showDismiss ? (
        <Button
          variant="ghost"
          onClick={dismiss}
          className="absolute -bottom-11 left-1/2 -translate-x-1/2 opacity-70 hover:opacity-100"
        >
          Not relevant
        </Button>
      ) : null}
    </div>
  );
}
